import { useMemo } from "react"

import { useTranslation } from "react-i18next"

import type { FeedbackContext } from "@/api/feedback-api"

import { Loader2, Send } from "@/components/icons"
import { Button } from "@/components/ui/button"

import { EscalationBadge } from "../escalation-badge"
import { type Lane, shouldEscalate } from "../schemas"
import { TransparencyBlock } from "../transparency-block"

type ReviewStepProps = {
  lane: Lane
  fields: Record<string, unknown>
  context: FeedbackContext
  evidencePreview?: { name: string; previewUrl: string } | null
  screenshotPreview?: { name: string; previewUrl: string } | null
  submitting: boolean
  /** User's consent to escalate, as set in the fields step. */
  escalationRequested: boolean
  onBack: () => void
  onSubmit: () => void
}

export function ReviewStep({
  lane,
  fields,
  context,
  evidencePreview,
  screenshotPreview,
  submitting,
  escalationRequested,
  onBack,
  onSubmit,
}: ReviewStepProps) {
  const { t } = useTranslation()
  const eligibility = useMemo(() => shouldEscalate(lane, fields), [lane, fields])
  // What will actually happen on submit — eligible AND the user opted in
  // (or didn't opt out, for incidents). Not raw eligibility.
  const finalEscalation = useMemo(
    () => ({ escalate: eligibility.escalate && escalationRequested, reasons: eligibility.reasons }),
    [eligibility, escalationRequested]
  )

  return (
    <div className="space-y-4">
      <EscalationBadge escalation={finalEscalation} lane={lane} />
      <TransparencyBlock
        context={context}
        evidencePreview={evidencePreview}
        fields={fields}
        lane={lane}
        screenshotPreview={screenshotPreview}
      />
      <div className="flex justify-between gap-2 pt-2">
        <Button disabled={submitting} variant="outline" onClick={onBack}>
          {t("common.back", "Back")}
        </Button>
        <Button disabled={submitting} onClick={onSubmit}>
          {submitting ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              {t("common.sending", "Sending...")}
            </>
          ) : (
            <>
              <Send className="size-4" />
              {t("feedback.actions.submit", "Submit")}
            </>
          )}
        </Button>
      </div>
    </div>
  )
}
